import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowRight, CheckCircle2, MessageCircle,
  Search, MousePointerClick, Send, ShieldCheck, Star,
} from 'lucide-react';
import logo from '../es-logo.png';
import Reveal from '../components/Reveal';
import PackageCard from '../components/PackageCard';
import BannerCarousel from '../components/BannerCarousel';
import Loader from '../components/Loader';
import useAsync from '../hooks/useAsync';
import { getContent } from '../lib/content';
import { fadeUp, stagger, viewportOnce } from '../theme/theme';
import { stats, testimonials } from '../data/sampleContent';

const steps = [
  { icon: Search, title: 'Browse Packages', text: 'Explore curated event packages across every occasion.' },
  { icon: MousePointerClick, title: 'Customize & Add-ons', text: 'Pick a package and layer on the services you love.' },
  { icon: Send, title: 'Send Inquiry', text: 'Submit your details — we ping you instantly on WhatsApp.' },
];

export default function Home() {
  const { data, loading } = useAsync(getContent, []);
  const categories = data?.categories || [];
  const featured = (data?.packages || []).slice(0, 6);
  const banners = data?.banners || [];

  return (
    <div className="overflow-hidden">
      {/* ============ HERO ============ */}
      <section className="relative">
        <div className="aurora absolute inset-0 -z-10" />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_-20%,rgba(14,124,196,0.25),transparent_60%)]" />

        <div className="section grid items-center gap-12 py-20 md:grid-cols-2 md:py-28">
          <motion.div
            variants={stagger(0.12)}
            initial="hidden"
            animate="show"
            className="space-y-7"
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-pill border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-ink-200 backdrop-blur"
            >
              <Sparkles size={15} className="text-spectrum-yellow" />
              Premium Event Planning & Package Booking
            </motion.span>

            <motion.h1
              variants={fadeUp}
              className="font-display text-4xl font-extrabold leading-[1.1] sm:text-5xl lg:text-6xl"
            >
              Crafting <span className="text-gradient animate-shimmer">Unforgettable</span> Moments
            </motion.h1>

            <motion.p variants={fadeUp} className="max-w-lg text-lg text-ink-300">
              From dream weddings to corporate summits — browse, customize and book
              complete event packages, then chat with us instantly on WhatsApp.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/packages" className="btn-primary">
                Explore Packages <ArrowRight size={18} />
              </Link>
              <Link to="/customize" className="btn-ghost">
                Build Your Event
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-6 pt-4">
              {stats.slice(0, 3).map((s) => (
                <div key={s.label}>
                  <p className="font-display text-2xl font-bold text-white">{s.value}</p>
                  <p className="text-sm text-ink-400">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Floating logo ring visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto grid h-80 w-80 place-items-center sm:h-96 sm:w-96"
          >
            <div className="absolute inset-0 animate-spin-slow rounded-full border border-dashed border-white/15" />
            <div className="absolute inset-6 rounded-full bg-hero-gradient opacity-30 blur-2xl animate-pulse-glow" />
            <motion.img
              src={logo}
              alt="Event Story"
              className="relative h-56 w-56 animate-float object-contain drop-shadow-[0_0_40px_rgba(14,124,196,0.6)]"
            />
            {['💍', '🎂', '🎤', '🎉'].map((e, i) => (
              <motion.span
                key={e}
                className="absolute text-3xl"
                style={{
                  top: `${20 + i * 20}%`,
                  left: i % 2 ? '85%' : '5%',
                }}
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.4 }}
              >
                {e}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============ PROMО BANNERS (admin-managed) ============ */}
      <BannerCarousel banners={banners} />

      {/* ============ CATEGORIES ============ */}
      <section className="section py-12">
        <Reveal className="mb-8 text-center">
          <h2 className="font-display text-3xl font-bold">Browse by Occasion</h2>
          <p className="mt-2 text-ink-300">Find the perfect package for every celebration.</p>
        </Reveal>
        {loading && <Loader label="Loading occasions…" />}
        <motion.div
          variants={stagger(0.06)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6"
        >
          {categories.map((c) => (
            <motion.div key={c.id} variants={fadeUp} whileHover={{ y: -6 }}>
              <Link
                to={`/packages?category=${c.id}`}
                className="glass flex flex-col items-center gap-2 p-5 text-center transition hover:border-white/30"
              >
                <span className="text-3xl">{c.icon}</span>
                <span className="text-sm font-medium text-white">{c.name}</span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ============ FEATURED PACKAGES ============ */}
      <section className="section py-16">
        <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl font-bold">Featured Packages</h2>
            <p className="mt-2 text-ink-300">Hand-picked experiences our clients love.</p>
          </div>
          <Link to="/packages" className="btn-ghost !py-2.5">
            View all <ArrowRight size={16} />
          </Link>
        </Reveal>
        {loading ? (
          <Loader label="Loading packages…" />
        ) : (
          <motion.div
            variants={stagger(0.08)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {featured.map((p) => (
              <PackageCard key={p.id} pkg={p} />
            ))}
          </motion.div>
        )}
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="section py-16">
        <Reveal className="mb-12 text-center">
          <h2 className="font-display text-3xl font-bold">How It Works</h2>
          <p className="mt-2 text-ink-300">Three simple steps to your perfect event.</p>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1} className="glass relative p-8 text-center">
              <span className="absolute right-5 top-4 font-display text-5xl font-extrabold text-white/5">
                0{i + 1}
              </span>
              <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-brand-gradient text-white shadow-brand-glow">
                <s.icon size={24} />
              </div>
              <h3 className="font-display text-lg font-bold">{s.title}</h3>
              <p className="mt-2 text-sm text-ink-300">{s.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ WHY US / STATS ============ */}
      <section className="section py-16">
        <div className="glass grid gap-8 p-10 md:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center">
              <p className="font-display text-4xl font-extrabold text-gradient">{s.value}</p>
              <p className="mt-1 text-sm text-ink-300">{s.label}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: ShieldCheck, t: 'Verified Vendors' },
            { icon: Star, t: 'Top-rated Service' },
            { icon: CheckCircle2, t: 'Transparent Pricing' },
            { icon: MessageCircle, t: 'Instant WhatsApp Support' },
          ].map((f, i) => (
            <Reveal key={f.t} delay={i * 0.06} className="glass flex items-center gap-3 p-4">
              <f.icon size={20} className="text-spectrum-cyan" />
              <span className="text-sm font-medium text-white">{f.t}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="section py-16">
        <Reveal className="mb-10 text-center">
          <h2 className="font-display text-3xl font-bold">Loved by Our Clients</h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.1} className="glass p-6">
              <div className="mb-4 flex gap-1 text-warning">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} size={15} className="fill-warning" />
                ))}
              </div>
              <p className="text-ink-200">“{t.text}”</p>
              <div className="mt-5 flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-semibold text-white">{t.name}</p>
                  <p className="text-xs text-ink-400">{t.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ CTA BAND ============ */}
      <section className="section py-16">
        <Reveal className="relative overflow-hidden rounded-xl bg-hero-gradient p-10 text-center md:p-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.18),transparent_50%)]" />
          <h2 className="relative font-display text-3xl font-extrabold text-white md:text-4xl">
            Ready to plan your next event?
          </h2>
          <p className="relative mx-auto mt-3 max-w-xl text-white/80">
            Tell us your vision. We’ll turn it into a story worth remembering.
          </p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/customize" className="btn bg-white text-ink-900 hover:scale-105">
              Start Customizing <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn-whatsapp">
              <MessageCircle size={18} /> Chat on WhatsApp
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
